/**
 * MarketingPagesManagement — admin shell for managing generic marketing pages.
 *
 * Lists pages from GET /admin/marketing-pages with status chips.
 * Allows creating, selecting (to edit), and deleting pages.
 * Editing a page loads PageBuilderManagement with the selected slug.
 *
 * New-page dialog:
 *   - slug input validated ^[a-z0-9-]+$
 *   - title input
 *   - live preview of public URL /p/<slug>
 *   - POST to create, surfaces 409 "slug exists" error
 *
 * Delete: confirm dialog, then DEL from API.
 * Unsaved-changes guard when switching pages (user must confirm).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, Chip, CircularProgress, Snackbar,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, useTheme, Paper, List, ListItem,
  ListItemButton, ListItemText, ListItemSecondaryAction,
  IconButton, Skeleton, Divider, InputAdornment,
} from '@mui/material';
import {
  Add, Delete, ChevronRight, OpenInNew, CheckCircle, Edit,
  ArrowBack,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { makePageBuilderApi } from './pageBuilder/pageBuilderApi';
import PageBuilderManagement from './PageBuilderManagement';
import {
  SectionHeader, GlassPaper, EmptyState,
} from './ui';
import { glass, ADMIN_GRADIENTS, ADMIN_COLORS } from './ui/glassTokens';

const MARKETING_API_BASE = '/admin/marketing-pages';
const SLUG_PATTERN = /^[a-z0-9-]+$/;

/**
 * Slugs the backend treats as reserved (system pages / route conflicts).
 * Mirror the backend RESERVED_SLUGS denylist here so the UI blocks deletion
 * and creation up-front without a round-trip.
 */
const RESERVED_SLUGS = new Set([
  'join-us-as-library',
  'login',
  'register',
  'admin-dashboard',
  'libraries',
  'api',
]);

// ─── helpers ──────────────────────────────────────────────────────────────────

function statusColor(status) {
  return status === 'published' ? 'success' : 'default';
}

function statusLabel(status) {
  return status === 'published' ? 'Published' : 'Draft';
}

// ─── PageListSkeleton ─────────────────────────────────────────────────────────

function PageListSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: 2 }} />
      ))}
    </Box>
  );
}

// ─── ConfirmDialog ─────────────────────────────────────────────────────────────

function ConfirmDialog({
  open, title, body, confirmLabel, confirmColor = 'primary',
  onConfirm, onClose, loading,
}) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">{body}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  confirmColor: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

// ─── NewPageDialog ────────────────────────────────────────────────────────────

function NewPageDialog({ open, onClose, onCreated }) {
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [apiError, setApiError] = useState('');

  const slugInputRef = useRef(null);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSlug('');
      setTitle('');
      setSlugError('');
      setTitleError('');
      setApiError('');
      setCreating(false);
      // Focus slug input after animation
      setTimeout(() => slugInputRef.current?.focus(), 120);
    }
  }, [open]);

  const validateSlug = useCallback((val) => {
    if (!val.trim()) return 'Slug is required';
    if (!SLUG_PATTERN.test(val)) return 'Slug may only contain lowercase letters, numbers, and hyphens';
    if (val.length > 100) return 'Slug must be under 100 characters';
    if (RESERVED_SLUGS.has(val.trim())) return 'This slug is reserved and cannot be used for a marketing page';
    return '';
  }, []);

  const handleSlugChange = useCallback((e) => {
    const val = e.target.value;
    setSlug(val);
    setApiError('');
    setSlugError(validateSlug(val));
  }, [validateSlug]);

  const handleTitleChange = useCallback((e) => {
    const val = e.target.value;
    setTitle(val);
    setTitleError(val.trim() ? '' : 'Title is required');
  }, []);

  const handleCreate = useCallback(async () => {
    const sErr = validateSlug(slug);
    const tErr = title.trim() ? '' : 'Title is required';
    setSlugError(sErr);
    setTitleError(tErr);
    if (sErr || tErr) return;

    try {
      setCreating(true);
      setApiError('');
      const pbApi = makePageBuilderApi(MARKETING_API_BASE);
      const newPage = await pbApi.createPage({ slug: slug.trim(), title: title.trim() });
      onCreated(newPage);
      onClose();
    } catch (err) {
      const msg = err.message || 'Failed to create page';
      // Detect 409 "slug already exists"
      if (msg.toLowerCase().includes('slug') || msg.toLowerCase().includes('exist') || err.status === 409) {
        setSlugError('This slug is already in use — choose a different one');
      } else {
        setApiError(msg);
      }
    } finally {
      setCreating(false);
    }
  }, [slug, title, validateSlug, onCreated, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !creating) handleCreate();
  }, [creating, handleCreate]);

  const publicUrl = slug.trim() ? `/p/${slug.trim()}` : null;

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>New marketing page</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
        {apiError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {apiError}
          </Alert>
        )}

        <TextField
          inputRef={slugInputRef}
          label="Slug"
          placeholder="e.g. summer-offer"
          value={slug}
          onChange={handleSlugChange}
          onKeyDown={handleKeyDown}
          error={Boolean(slugError)}
          helperText={
            slugError ||
            'Lowercase letters, numbers, hyphens only. Used in the public URL.'
          }
          fullWidth
          size="small"
          disabled={creating}
          InputProps={{
            startAdornment: <InputAdornment position="start">/p/</InputAdornment>,
          }}
        />

        <TextField
          label="Page title"
          placeholder="e.g. Summer Offer — Join BML"
          value={title}
          onChange={handleTitleChange}
          onKeyDown={handleKeyDown}
          error={Boolean(titleError)}
          helperText={titleError || 'Shown in the page editor header and browser tab'}
          fullWidth
          size="small"
          disabled={creating}
        />

        {publicUrl && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, wordBreak: 'break-all' }}>
              Public URL: <strong>{publicUrl}</strong>
            </Typography>
            <Tooltip title="Open in new tab (page must be published first)">
              <IconButton size="small" href={publicUrl} target="_blank" rel="noopener noreferrer">
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={creating}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || Boolean(slugError) || Boolean(titleError)}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <Add />}
          sx={{
            borderRadius: 2,
            background: ADMIN_GRADIENTS.accent,
            '&:hover': { background: ADMIN_GRADIENTS.accentShort },
          }}
        >
          {creating ? 'Creating…' : 'Create page'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

NewPageDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

// ─── PageListItem ─────────────────────────────────────────────────────────────

function PageListItem({ page, selected, onSelect, onDelete }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isReserved = RESERVED_SLUGS.has(page.slug);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: selected
          ? `1.5px solid ${ADMIN_COLORS.accentIndigo}`
          : `1px solid ${theme.palette.divider}`,
        bgcolor: selected
          ? isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)'
          : 'background.paper',
        transition: 'all 0.15s ease',
      }}
    >
      <ListItem
        disablePadding
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1 }}>
            <Tooltip title={`Open /p/${page.slug} in new tab`}>
              <IconButton
                size="small"
                href={`/p/${page.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isReserved ? 'System page — cannot be deleted' : 'Delete this page permanently'}>
              {/* span wrapper required so Tooltip works on a disabled element */}
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={isReserved}
                  aria-label={isReserved ? 'System page — cannot be deleted' : `Delete page ${page.slug}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(page);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      >
        <ListItemButton onClick={() => onSelect(page)} sx={{ py: 1.5, pl: 2, pr: 12 }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 260 }}>
                  {page.title || page.slug}
                </Typography>
                <Chip
                  size="small"
                  label={statusLabel(page.status)}
                  color={statusColor(page.status)}
                  icon={page.status === 'published'
                    ? <CheckCircle sx={{ fontSize: '13px !important' }} />
                    : <Edit sx={{ fontSize: '13px !important' }} />
                  }
                  sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" component="span">
                /p/{page.slug}
                {page.updated_at
                  ? ` · updated ${format(new Date(page.updated_at), 'd MMM yyyy')}`
                  : ''}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
    </Paper>
  );
}

PageListItem.propTypes = {
  page: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ─── MarketingPagesManagement (default export) ────────────────────────────────

export default function MarketingPagesManagement() {
  const theme = useTheme();
  const g = glass(theme);

  // ── Pages list state ─────────────────────────────────────────────────────────
  const [pages, setPages] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // ── Selected page (editor) state ─────────────────────────────────────────────
  const [selectedPage, setSelectedPage] = useState(null);

  // ── Dirty guard ──────────────────────────────────────────────────────────────
  // PageBuilderManagement manages its own dirty state internally; we track
  // whether a switch-away confirmation should be shown via a ref-based flag.
  // For simplicity we always show the guard when a page is selected and user
  // switches — they can dismiss if no actual edits were made.
  const [pendingSwitchPage, setPendingSwitchPage] = useState(null);
  const [switchGuardOpen, setSwitchGuardOpen] = useState(false);

  // ── Dialog state ─────────────────────────────────────────────────────────────
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // page object
  const [deleting, setDeleting] = useState(false);

  // ── Snackbar ─────────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar(s => ({ ...s, open: false }));
  }, []);

  // ── Fetch page list ──────────────────────────────────────────────────────────
  const fetchPages = useCallback(async () => {
    try {
      setListLoading(true);
      setListError(null);
      const pbApi = makePageBuilderApi(MARKETING_API_BASE);
      const data = await pbApi.listPages();
      setPages(Array.isArray(data) ? data : []);
    } catch (err) {
      setListError(err.message || 'Failed to load pages');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // ── Select a page (with dirty guard when another is already selected) ────────
  const handleSelectPage = useCallback((page) => {
    if (selectedPage && selectedPage.slug !== page.slug) {
      setPendingSwitchPage(page);
      setSwitchGuardOpen(true);
    } else {
      setSelectedPage(page);
    }
  }, [selectedPage]);

  const handleConfirmSwitch = useCallback(() => {
    if (pendingSwitchPage) {
      setSelectedPage(pendingSwitchPage);
    }
    setPendingSwitchPage(null);
    setSwitchGuardOpen(false);
  }, [pendingSwitchPage]);

  const handleCancelSwitch = useCallback(() => {
    setPendingSwitchPage(null);
    setSwitchGuardOpen(false);
  }, []);

  // ── Back to list ─────────────────────────────────────────────────────────────
  const handleBack = useCallback(() => {
    setPendingSwitchPage(null);
    setSwitchGuardOpen(false);
    setSelectedPage(null);
    // Refresh list to pick up any status changes from the editor
    fetchPages();
  }, [fetchPages]);

  // ── New page created ─────────────────────────────────────────────────────────
  const handlePageCreated = useCallback((newPage) => {
    setPages(prev => [newPage, ...prev]);
    setSelectedPage(newPage);
    showSnackbar(`Page "${newPage.slug}" created — start editing below`);
  }, [showSnackbar]);

  // ── Delete page ───────────────────────────────────────────────────────────────
  const handleDeletePage = useCallback((page) => {
    setDeleteTarget(page);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    // Guard: never delete reserved slugs even if somehow triggered
    if (RESERVED_SLUGS.has(deleteTarget.slug)) {
      showSnackbar('System pages cannot be deleted', 'error');
      setDeleteTarget(null);
      return;
    }
    try {
      setDeleting(true);
      const pbApi = makePageBuilderApi(MARKETING_API_BASE);
      await pbApi.deletePage(deleteTarget.slug);
      // Only update local state after a confirmed successful delete
      setPages(prev => prev.filter(p => p.slug !== deleteTarget.slug));
      if (selectedPage?.slug === deleteTarget.slug) {
        setSelectedPage(null);
      }
      showSnackbar(`Page "${deleteTarget.slug}" deleted`);
    } catch (err) {
      // Page stays in the list; surface the error so the admin knows what happened
      showSnackbar(err.message || 'Failed to delete page', 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, selectedPage, showSnackbar]);

  // ─────────────────────────────────────────────────────────────────────────────
  // When a page is selected, render the builder for it
  // ─────────────────────────────────────────────────────────────────────────────

  if (selectedPage) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', pb: 8 }}>
        {/* Back nav */}
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            All marketing pages
          </Button>
        </Box>

        <PageBuilderManagement
          slug={selectedPage.slug}
          apiBase={MARKETING_API_BASE}
        />

        {/* Switch-away guard */}
        <ConfirmDialog
          open={switchGuardOpen}
          title="Unsaved changes?"
          body="You may have unsaved changes on this page. Switch away without saving? Your draft will not be lost — it is already saved on the server if you last clicked Save Draft."
          confirmLabel="Switch away"
          confirmColor="warning"
          loading={false}
          onConfirm={handleConfirmSwitch}
          onClose={handleCancelSwitch}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.severity === 'error' ? null : 4000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%', maxWidth: 480, borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Page list view
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', pb: 8 }}>
      {/* Header */}
      <SectionHeader
        eyebrow="Marketing"
        title="Marketing Pages"
        description="Create and manage admin-controlled marketing pages. Each page has its own public URL (/p/<slug>) and can be published or kept as a draft."
        action={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewPageOpen(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: ADMIN_GRADIENTS.accent,
              '&:hover': { background: ADMIN_GRADIENTS.accentShort },
            }}
          >
            New page
          </Button>
        }
      />

      {/* Load error */}
      {listError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          Could not load pages — {listError}. {' '}
          <Button size="small" onClick={fetchPages}>Retry</Button>
        </Alert>
      )}

      {/* Page list */}
      <GlassPaper sx={{ p: 2, borderRadius: 3 }}>
        {listLoading ? (
          <PageListSkeleton />
        ) : pages.length === 0 ? (
          <EmptyState
            title="No marketing pages yet"
            description="Create your first page to get started. Set a slug, add sections in the builder, and publish."
            action={
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setNewPageOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Create first page
              </Button>
            }
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {pages.length} page{pages.length !== 1 ? 's' : ''} — click to edit
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {pages.filter(p => p.status === 'published').length} published
              </Typography>
            </Box>
            <Divider sx={{ mb: 0.5 }} />
            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {pages.map((page) => (
                <PageListItem
                  key={page.slug}
                  page={page}
                  selected={selectedPage?.slug === page.slug}
                  onSelect={handleSelectPage}
                  onDelete={handleDeletePage}
                />
              ))}
            </List>
          </Box>
        )}
      </GlassPaper>

      {/* Dialogs */}
      <NewPageDialog
        open={newPageOpen}
        onClose={() => setNewPageOpen(false)}
        onCreated={handlePageCreated}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.title || deleteTarget?.slug}"?`}
        body={`This will permanently delete the page /p/${deleteTarget?.slug} and all its content. This action cannot be undone.`}
        confirmLabel="Delete page"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={switchGuardOpen}
        title="Unsaved changes?"
        body="You may have unsaved changes on the current page. Switch away without saving?"
        confirmLabel="Switch away"
        confirmColor="warning"
        loading={false}
        onConfirm={handleConfirmSwitch}
        onClose={handleCancelSwitch}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 480, borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
